import axios from 'axios';

/** Fallback used when no runtime config `web.ccagent.conv_base_url` is set (e.g. localhost). */
const DEFAULT_CONV_UI_BASE_URL = 'https://dev-riderapp.ecolane.com/conv-phase2';

// Module-level thread ID — persists across messages in the same session
let _currentThreadId: string | null = null;

export const resetThreadId = (): void => {
  _currentThreadId = null;
};

export interface ChatResponse {
  response: any;
}

export interface VoiceStreamEvent {
  transcription?: string;
  response?: unknown;
  type?: string;
  error?: string;
  audio_chunk?: string;
}

export interface VoiceStreamResult {
  transcription: string;
  payload: VoiceStreamEvent;
  audioChunks: string[];
}

export const sendMessage = async (question: string, convUiBaseUrl?: string): Promise<ChatResponse> => {
  const apiUrl = `${convUiBaseUrl ?? DEFAULT_CONV_UI_BASE_URL}/query`;
  const response = await axios.post(apiUrl, {
    question,
    thread_id: _currentThreadId,   // null on first turn → server generates one
  });
  const data = response.data;

  // Store thread_id from response for next turn
  if (data?.thread_id) {
    _currentThreadId = data.thread_id;
  }

  if (data.response) {
    let responseData = data.response;

    // Keep parsing until the response is not a stringified JSON
    while (typeof responseData === 'string') {
      try {
        responseData = JSON.parse(responseData);
      } catch (error) {
        // Not a JSON string, break the loop
        break;
      }
    }

    // If after all parsing, the result is a string, wrap it in the object structure the UI expects
    if (typeof responseData === 'string') {
      responseData = { type: 'text', raw: responseData };
    } else if (typeof responseData === 'object' && responseData !== null) {
      // If the object has a 'response' property, it's likely the nested structure we've seen.
      // We'll prefer the 'response' property's content.
      if (responseData.response) {
        let nestedResponse = responseData.response;
        // Also check if this nested response is a stringified JSON
        while (typeof nestedResponse === 'string') {
          try {
            nestedResponse = JSON.parse(nestedResponse);
          } catch (error) {
            break;
          }
        }

        // If the deeply nested response is a string, wrap it.
        if (typeof nestedResponse === 'string') {
          responseData = { type: 'text', raw: nestedResponse };
        } else {
          responseData = nestedResponse;
        }
      }
    }

    data.response = responseData;
  }

  return data;
};

export const sendAudio = async (audioBlob: Blob, convUiBaseUrl?: string): Promise<ChatResponse> => {
  const apiUrl = `${convUiBaseUrl ?? DEFAULT_CONV_UI_BASE_URL}/query`;
  const formData = new FormData();
  formData.append('audio', audioBlob, 'recording.wav');

  const response = await axios.post(apiUrl, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  const data = response.data;

  if (data.response) {
    let responseData = data.response;

    // Keep parsing until the response is not a stringified JSON
    while (typeof responseData === 'string') {
      try {
        responseData = JSON.parse(responseData);
      } catch (error) {
        // Not a JSON string, break the loop
        break;
      }
    }
    
    // If after all parsing, the result is a string, wrap it in the object structure the UI expects
    if (typeof responseData === 'string') {
      responseData = { type: 'text', raw: responseData };
    } else if (typeof responseData === 'object' && responseData !== null) {
      // If the object has a 'response' property, it's likely the nested structure we've seen.
      // We'll prefer the 'response' property's content.
      if (responseData.response) {
        let nestedResponse = responseData.response;
        // Also check if this nested response is a stringified JSON
        while (typeof nestedResponse === 'string') {
          try {
            nestedResponse = JSON.parse(nestedResponse);
          } catch (error) {
            break;
          }
        }

        // If the deeply nested response is a string, wrap it.
        if (typeof nestedResponse === 'string') {
          responseData = { type: 'text', raw: nestedResponse };
        } else {
          responseData = nestedResponse;
        }
      }
    }
    
    data.response = responseData;
  }

  return data;
};

/**
 * Sends a recorded audio blob to the environment-resolved voice endpoint using streaming SSE mode.
 * Dev: POST https://dev-riderapp.ecolane.com/conv-ui/voice
 * QA/Demo/default: POST https://qa-riderapp.ecolane.com/conv-ui-qa/voice
 *
 * Per voice-guide.md:
 *  - Request: multipart/form-data — do NOT set Content-Type manually
 *  - Fields: file (required), mime_type=audio/pcm, input_audio_format=pcm16, stream=true
 *  - SSE events:
 *      data: {"transcription":"..."}          ← first event
 *      data: {"response":...,"type":"..."}    ← second event
 *      data: [DONE]                           ← final event
 *      data: {"error":"..."}                  ← on failure
 *  - HTTP 400: audio missing/empty
 *  - HTTP 502: transcription or backend processing failed
 */
export const sendVoice = async (audioBlob: Blob, convUiBaseUrl?: string): Promise<VoiceStreamResult> => {
  const voiceUrl = `${convUiBaseUrl ?? DEFAULT_CONV_UI_BASE_URL}/voice`;
  const formData = new FormData();

  // Use the actual MIME type of the recorded blob so the server can decode it
  // correctly. MediaRecorder typically produces audio/webm or audio/ogg, not PCM.
  // Only send input_audio_format=pcm16 when the blob is truly raw PCM.
  const actualMime = audioBlob.type || 'audio/webm';
  const isPcm = actualMime.includes('pcm');

  formData.append('file', audioBlob, isPcm ? 'voice-input.pcm' : 'voice-input.webm');
  formData.append('mime_type', actualMime);
  if (isPcm) {
    formData.append('input_audio_format', 'pcm16');
  }
  formData.append('stream', 'true');

  const response = await fetch(voiceUrl, {
    method: 'POST',
    body: formData,
    // Do NOT set Content-Type — the browser sets it with the correct multipart boundary
  });

  if (!response.ok) {
    const statusText =
      response.status === 400
        ? 'Audio file is missing or empty (400).'
        : response.status === 502
          ? 'Transcription or backend voice processing failed (502).'
          : `Voice API error: ${response.status}`;
    throw new Error(statusText);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Failed to read streaming response body.');
  }

  const decoder = new TextDecoder();
  let buffer = '';
  let transcription = '';
  let payload: VoiceStreamEvent = {};
  const audioChunks: string[] = [];
  let done = false;

  while (!done) {
    const { done: streamDone, value } = await reader.read();
    if (streamDone) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    // Retain the last (potentially incomplete) line for the next chunk
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed.startsWith('data:')) continue;

      const raw = trimmed.slice('data:'.length).trim();
      if (raw === '[DONE]') {
        done = true;
        break;
      }

      try {
        const event: VoiceStreamEvent = JSON.parse(raw);

        if (event.error) {
          throw new Error(event.error);
        }

        // First event: transcription text
        if (event.transcription) {
          transcription = event.transcription;
        }

        // Second event: dashboard/assistant response
        if (event.response !== undefined || event.type !== undefined) {
          payload = event;
        }

        // Collect audio chunks
        if (event.audio_chunk) {
          audioChunks.push(event.audio_chunk);
        }
      } catch (parseErr) {
        // Re-throw real errors; skip malformed SSE lines
        if (parseErr instanceof Error && parseErr.message !== 'Unexpected end of JSON input') {
          throw parseErr;
        }
      }
    }
  }

  return { transcription, payload, audioChunks };
};
